import type { ValidationErrors } from 'webapp-types';

type Context = object | string; // any object with the validated props (or an Ember `get`), or a single value
type PropName = string;

interface RuleOpts {
	minValue?: number;
	maxValue?: number;
	insensitive?: boolean;
	regex?: string;
	regexMessage?: string;
	fn?: (value: unknown) => boolean;
	errText?: string;
}

export type ValidationRule =
	| {
			rule: string;
			opts: RuleOpts;
			field?: PropName;
	  }
	| string;

export type ValidationFieldConfig = ValidationRule[] | { rules: ValidationRule[]; label?: string };
export type ValidationFieldsConfig = Record<string, ValidationFieldConfig>;

// by default, we pass arg1 as context and arg2 as string of the prop name
// but also we want to support validation of single strings (when prop name is not defined)
function getValueFromParams(context: Context, propName?: PropName): unknown {
	let value;
	if (propName && typeof context !== 'string') {
		const objectContext = context as { get?: (propName: PropName) => unknown } & Record<
			PropName,
			unknown
		>;
		if (objectContext.get) {
			value = objectContext.get(propName);
		} else {
			value = objectContext[propName];
		}
	} else {
		value = context;
	}
	return value;
}

function validateByRules(
	context: Context,
	rulesArray: ValidationRule[],
	field: PropName,
	fieldLabel?: string,
): ValidationErrors<string> {
	const errors: ValidationErrors<string> = {};
	let opts: RuleOpts = {},
		isValid,
		field1: PropName | undefined;

	rulesArray.forEach((rule: string | ValidationRule) => {
		let errorMsg;

		if (typeof rule === 'object') {
			if (rule.opts) {
				opts = rule.opts;
			}
			field1 = rule.field || undefined;
			rule = rule.rule;
		}

		// one error per field
		if (!errors[field]) {
			switch (rule) {
				case 'required':
					isValid = validateRequired(context, field);
					updateErrors(
						errors,
						field,
						isValid,
						'Field ' + (fieldLabel || field) + ' is required.',
					);
					break;
				case 'email':
					isValid = validateEmail(context, field);
					updateErrors(errors, field, isValid, 'Email is not valid.');
					break;
				case 'numeric':
					isValid = validateNumeric(context, field);
					updateErrors(
						errors,
						field,
						isValid,
						'Field ' + (fieldLabel || field) + ' must have numeric value.',
					);
					break;
				case 'numeric-between-values':
					isValid = validateNumericBetweenValues(
						context,
						opts.minValue!,
						opts.maxValue!,
						field,
					);
					updateErrors(
						errors,
						field,
						isValid,
						'Field ' +
							(fieldLabel || field) +
							' must be between ' +
							opts.minValue +
							' and ' +
							opts.maxValue +
							'.',
					);
					break;
				case 'int-positive':
					isValid = validateInteger(context, field, true);
					updateErrors(
						errors,
						field,
						isValid,
						'Field ' + (fieldLabel || field) + ' must be a positive number.',
					);
					break;
				case 'metricKey':
					isValid = validateMetricKey(context, field);
					updateErrors(
						errors,
						field,
						isValid,
						'Field ' + (fieldLabel || field) + ' is invalid.',
					);
					break;
				case 'match':
					isValid = validateEqual(context, field, field1, opts.insensitive);
					updateErrors(
						errors,
						field,
						isValid,
						field + ' and ' + field1 + ' does not match.',
					);
					if (field1) {
						updateErrors(errors, field1, isValid, ' ');
					}
					break;
				case 'hexColor':
					isValid = validateHex(context, field);
					updateErrors(errors, field, isValid, field + ' is not valid color code.');
					break;
				case 'customRegex':
					isValid = validateCustomRegex(context, opts.regex!, field);
					updateErrors(
						errors,
						field,
						isValid,
						opts.regexMessage || field + ' does not match the pattern provided.',
					);
					break;
				case 'url':
					isValid = validateUrl(context, field);
					updateErrors(
						errors,
						field,
						isValid,
						field + ' is not a valid URL (e.g. https://databox.com)',
					);
					break;
				case 'databox-password':
					isValid = true;
					if (!validateMinLength(context, 8, field)) {
						errorMsg = 'Password too short. It has to have at least 8 characters.';
						isValid = false;
					} else if (!validateCustomRegex(context, '\\d', field)) {
						errorMsg = 'Password must contain at least one digit.';
						isValid = false;
					}
					updateErrors(errors, field, isValid, errorMsg);
					break;
				case 'custom-function':
					isValid = opts.fn!(getValueFromParams(context, field));
					updateErrors(
						errors,
						field,
						isValid,
						opts.errText || field + ' is not a valid value.',
					);
					break;
			}
		}
	});

	return errors;
}

function validateForm(
	context: Context,
	fields: ValidationFieldsConfig,
	prefix?: string,
): ValidationErrors<string> {
	const errors: ValidationErrors<string> = {};
	let error,
		rules: ValidationRule[] = ['required'];

	if (!prefix) {
		prefix = '';
	}

	Object.keys(fields).forEach((field) => {
		const fieldConfig = fields[field];
		let label: string | undefined;

		if (fieldConfig) {
			if (Array.isArray(fieldConfig)) {
				rules = fieldConfig;
			} else {
				rules = fieldConfig.rules;
				label = fieldConfig.label;
			}
		}
		error = validateByRules(context, rules, prefix + field, label);
		const fieldError = error[prefix + field];
		if (fieldError) {
			errors[field] = [{ message: fieldError[0]!.message }];
		}
	});

	return errors;
}

function updateErrors(
	errors: ValidationErrors<string>,
	name: PropName,
	isValid: boolean,
	errorMessage?: string,
): void {
	if (!errorMessage) {
		errorMessage = 'Error';
	}

	if (isValid) {
		delete errors[name];
		return;
	}

	errors[name] = [{ message: errorMessage }];
}

function validateNotEmpty(value: unknown): boolean {
	let isValid = true;
	if (value instanceof Array) {
		isValid = value.length > 0;
	} else if (typeof value === 'string') {
		isValid = value.trim().length > 0;
	} else {
		isValid = !!value || value === 0;
	}
	return isValid;
}

function validateRequired(context: Context, name?: PropName): boolean {
	return validateNotEmpty(getValueFromParams(context, name));
}

function validateEqual(
	context: Context,
	name1?: PropName,
	name2?: PropName,
	insensitive?: boolean,
) {
	let val1 = getValueFromParams(context, name1),
		val2 = getValueFromParams(context, name2);
	if (insensitive) {
		val1 = String(val1).toUpperCase();
		val2 = String(val2).toUpperCase();
	}
	return val1 === val2;
}

function validateEmail(context: Context, name?: PropName): boolean {
	const emailMatch = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	return emailMatch.test(String(getValueFromParams(context, name)));
}

function validateCustomRegex(context: Context, regex: string, name?: PropName): boolean {
	const pattern = new RegExp(regex);
	return pattern.test(String(getValueFromParams(context, name)));
}

function validateNumeric(context: Context, name?: PropName): boolean {
	return !isNaN(Number(getValueFromParams(context, name)));
}

function validateNumericBetweenValues(
	context: Context,
	minValue: number,
	maxValue: number,
	name?: PropName,
): boolean {
	const value = Number(getValueFromParams(context, name));
	return validateNumeric(context, name) && value >= minValue && value <= maxValue;
}

function validateInteger(context: Context, name?: PropName, onlyPositive?: boolean): boolean {
	const val = getValueFromParams(context, name),
		isInteger = String(val) === String(Number(val));
	let isValid = isInteger;
	if (onlyPositive) {
		isValid = isInteger && Number(val) > 0;
	}
	return isValid;
}

function validateMetricKey(context: Context, name?: PropName): boolean {
	return (
		String(getValueFromParams(context, name)).indexOf('@') > 0 ||
		String(getValueFromParams(context, name)).indexOf('|') > 0
	);
}

function validateHex(context: Context, name: PropName): boolean {
	const hexColorMatch = /(^[0-9A-Fa-f]{6}$)|(^[0-9A-Fa-f]{3}$)/;
	return hexColorMatch.test(String(getValueFromParams(context, name)));
}

function validateIp(context: Context, name?: PropName): boolean {
	const ipMatch =
		/^(((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|\*)\.){2}((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)|\*))|(((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){1,3}\*))$/;
	return ipMatch.test(String(getValueFromParams(context, name)));
}

function validateUrl(context: Context, name?: PropName): boolean {
	const url = String(getValueFromParams(context, name)),
		a: HTMLAnchorElement = document.createElement('a');
	a.href = url;

	if (!a.host) {
		return false;
	}

	return (
		a.host !== window.location.host || // host correctly filled in
		url.startsWith('http')
	); // also support same host URLs -> has to be full path
}

function validateMinLength(context: Context, minLength: number, name?: PropName): boolean {
	const val = getValueFromParams(context, name),
		valueLength = String(val).length;
	return !!val && valueLength >= minLength;
}

export { validateByRules, validateForm, validateEmail, validateIp, validateUrl };
